import React, { useContext, useState } from 'react';
import styled from 'styled-components';

import Button from 'kingpin/atoms/Button';
import TextInput from 'kingpin/atoms/TextInput';
import Typography from 'kingpin/atoms/Typography';
import Inputs from 'components/Inputs';
import { useIsCurrentUserFleetOpsStaff } from 'components/Common/FleetOpsStaffOnly';
import { InsightsChatContext } from 'contextProviders/InsightsChatProvider';
import useMetricOptions from 'hooks/useMetricOptions';
import {
  FEEDBACK_FAILURE_CATEGORIES,
  FeedbackCategory,
  FeedbackExpectation,
  feedbackCategoryLabel,
  MessageFeedback,
} from 'services/insightsFeedback';
import { AnswerSource } from 'services/streamInsightsQuery';

// Per-answer feedback capture (training loop, P1). 👍 submits immediately; 👎
// submits a "down" signal and reveals an optional note that sends a follow-up
// correction. Staff and customers share this; the server stamps who is staff.
// For staff the 👎 panel is richer (design §4): a failure-category tag and the
// structured `expected` routing (the metric/dataset/field that SHOULD have been
// used) travel with the correction, seeding triage + the golden eval. The server
// drops category/expected for non-staff callers, so it is safe to send them.
// See functions/docs/insights-training-design.md §4.

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const NoteArea = styled.textarea`
  width: 100%;
  min-height: 52px;
  resize: vertical;
  padding: 8px;
  border-radius: 6px;
  border: 1px solid var(--color-border-primary);
  background: var(--color-bg-tertiary);
  color: var(--color-text-primary);
  font-family: inherit;
  font-size: 12px;

  &::placeholder {
    color: var(--color-text-muted);
  }
`;

// Matches the kingpin TextInput tokens so the tag reads correctly in both themes.
const CategorySelect = styled.select`
  padding: 4px 6px;
  border-radius: 6px;
  border: 1px solid var(--color-border-primary);
  background: var(--color-bg-tertiary);
  color: var(--color-text-primary);
  font-family: inherit;
  font-size: 12px;
`;

const NOTE_MAX = 2000;

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography.Body type="Body 12" color="var(--color-text-secondary)">
    {children}
  </Typography.Body>
);

const InsightsMessageFeedback = ({
  messageId,
  feedback,
  sources,
}: {
  messageId: string;
  feedback?: MessageFeedback;
  sources?: AnswerSource[];
}) => {
  const { submitFeedback } = useContext(InsightsChatContext);
  const isStaff = useIsCurrentUserFleetOpsStaff();
  const { metricOptionsNoSpecials } = useMetricOptions();
  const [note, setNote] = useState('');
  const [noteSent, setNoteSent] = useState(false);
  // Staff-only structured refinements sent with the correction.
  const [category, setCategory] = useState<FeedbackCategory | ''>('');
  const [expectedMetric, setExpectedMetric] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [dataset, setDataset] = useState('');
  const [field, setField] = useState('');

  // Not yet reacted — offer the two choices.
  if (!feedback) {
    return (
      <Wrap>
        <Row>
          <Typography.Body type="Body 12" color="var(--color-text-secondary)">
            Was this helpful?
          </Typography.Body>
          <Button
            size="Small"
            type="Ghost"
            icon="thumb-up"
            iconColor="var(--color-status-success)"
            onClick={() => submitFeedback({ messageId, verdict: 'up' })}
            testId="insights-feedback-up"
          />
          <Button
            size="Small"
            type="Ghost"
            icon="thumb-down"
            iconColor="var(--color-status-danger)"
            onClick={() => submitFeedback({ messageId, verdict: 'down' })}
            testId="insights-feedback-down"
          />
        </Row>
      </Wrap>
    );
  }

  // Reacted. After a 👎 (and before a note is sent), offer an optional note —
  // for staff this is the correction itself, plus a tag and expected routing.
  const showNote = feedback.verdict === 'down' && !noteSent;
  const usedSource = sources?.[0];

  const onSend = () => {
    const expected: FeedbackExpectation = {};
    if (expectedMetric) {
      expected.metricId = expectedMetric.id;
    }
    if (dataset.trim()) {
      expected.dataset = dataset.trim();
    }
    if (field.trim()) {
      expected.field = field.trim();
    }
    const hasExpected = Object.keys(expected).length > 0;
    submitFeedback({
      messageId,
      verdict: 'correction',
      correctionText: note.trim(),
      ...(category ? { category } : {}),
      ...(hasExpected ? { expected } : {}),
    });
    setNoteSent(true);
  };

  return (
    <Wrap>
      <Typography.Body type="Body 12" color="var(--color-text-secondary)">
        Thanks for your feedback.
      </Typography.Body>
      {showNote && (
        <>
          {isStaff && (
            <>
              {usedSource && (
                <FieldLabel>
                  {`Agent used: ${usedSource.metricName ?? usedSource.metricId}${
                    usedSource.dataset ? ` · ${usedSource.dataset}` : ''
                  }`}
                </FieldLabel>
              )}
              <Field>
                <FieldLabel>Failure tag</FieldLabel>
                <CategorySelect
                  aria-label="Failure tag"
                  value={category}
                  onChange={(e) =>
                    setCategory(e.target.value as FeedbackCategory)
                  }
                  data-testid="insights-feedback-category"
                >
                  <option value="" disabled>
                    Tag…
                  </option>
                  {FEEDBACK_FAILURE_CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {feedbackCategoryLabel(c)}
                    </option>
                  ))}
                </CategorySelect>
              </Field>
              <Field as="div">
                <FieldLabel>Expected metric</FieldLabel>
                <Inputs.Dropdown
                  unsetWidth
                  placeholder="Metric that should have been used"
                  selectedLabel={expectedMetric?.name}
                  options={metricOptionsNoSpecials.map(
                    (m): DropdownOption => ({
                      key: m.id,
                      label: m.name,
                      isSelected: m.id === expectedMetric?.id,
                      onSelected: () =>
                        setExpectedMetric({ id: m.id, name: m.name }),
                    }),
                  )}
                />
              </Field>
              <Field>
                <FieldLabel>Expected dataset</FieldLabel>
                <TextInput
                  value={dataset}
                  onChange={(e) => setDataset(e.target.value)}
                  placeholder="e.g. orders (optional)"
                  data-testid="insights-feedback-dataset"
                />
              </Field>
              <Field>
                <FieldLabel>Expected field</FieldLabel>
                <TextInput
                  value={field}
                  onChange={(e) => setField(e.target.value)}
                  placeholder="e.g. completionDate (optional)"
                  data-testid="insights-feedback-field"
                />
              </Field>
            </>
          )}
          <NoteArea
            value={note}
            maxLength={NOTE_MAX}
            placeholder="Provide feedback here"
            onChange={(e) => setNote(e.target.value)}
            data-testid="insights-feedback-note"
          />
          <Row>
            <Button
              size="Small"
              type="Primary"
              label="Send"
              isDisabled={note.trim() === ''}
              onClick={onSend}
              testId="insights-feedback-send-note"
            />
          </Row>
        </>
      )}
    </Wrap>
  );
};

export default InsightsMessageFeedback;
